import {
  ArrowLeft,
  ArrowRight,
  Download,
  MonitorSmartphone,
  RotateCcw,
  RotateCw,
  ScanSearch,
  Sparkles,
} from 'lucide-react';
import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MAX_FINE_ROTATION_DEG } from '@/tools/image-engine';
import { TOOLS_AI_ENHANCE, holds, useToolsPermissions } from '@/tools/use-tools-permissions';
import { CornerEditor } from './CornerEditor';
import { LivePreview } from './LivePreview';
import { PRESETS, presetById } from './presets';
import type { ImagesFlow } from './use-images-flow';

const ENHANCEMENT_KEYS = ['illumination', 'contrast', 'sharpness', 'noise'] as const;

export function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function LocalNote() {
  const { t } = useTranslation('tools');
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <MonitorSmartphone aria-hidden="true" className="size-4 shrink-0" />
      {t('images.localNote')}
    </p>
  );
}

export function LoadStep({ flow }: { readonly flow: ImagesFlow }) {
  const { t } = useTranslation('tools');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const { onFile, onDrop, loadError } = flow.loader;
  return (
    <div
      onDragOver={(event) => event.preventDefault()}
      onDrop={onDrop}
      className="flex min-h-56 flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-border p-6 text-center"
    >
      <p className="text-sm text-muted-foreground">{t('images.load.drop')}</p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button type="button" onClick={() => fileInputRef.current?.click()}>
          {t('images.load.choose')}
        </Button>
        <Button type="button" variant="outline" onClick={() => cameraInputRef.current?.click()}>
          {t('images.load.takePhoto')}
        </Button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void onFile(e)}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void onFile(e)}
      />
      {loadError && (
        <p role="alert" className="text-sm text-destructive">
          {t('images.load.readError')}
        </p>
      )}
    </div>
  );
}

function StraightenControls({ flow }: { readonly flow: ImagesFlow }) {
  const { t } = useTranslation('tools');
  const uid = useId();
  const { session, corr } = flow;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={t('images.straighten.rotateLeft')}
        onClick={() => void session.rotateQuarter(-1)}
      >
        <RotateCcw />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={t('images.straighten.rotateRight')}
        onClick={() => void session.rotateQuarter(1)}
      >
        <RotateCw />
      </Button>
      <label
        htmlFor={`${uid}-fine`}
        className="flex items-center gap-2 text-sm text-muted-foreground"
      >
        {t('images.straighten.fine')}
        <input
          id={`${uid}-fine`}
          type="range"
          min={-MAX_FINE_ROTATION_DEG}
          max={MAX_FINE_ROTATION_DEG}
          step={0.5}
          value={corr.rotation}
          onChange={(e) => corr.setRotation(Number(e.target.value))}
          className="h-11 w-36 accent-[var(--color-brand)]"
        />
        <span className="tabular-nums text-foreground">{corr.rotation}°</span>
      </label>
      <span className="grow" />
      <Button
        type="button"
        variant="outline"
        onClick={() => void session.detect().then((found) => flow.setDetectMissed(!found))}
      >
        <ScanSearch aria-hidden="true" />
        {t('images.straighten.detect')}
      </Button>
    </div>
  );
}

export function StraightenStep({ flow }: { readonly flow: ImagesFlow }) {
  const { t } = useTranslation('tools');
  const { session } = flow;
  if (!session.image || !session.corners) return null;
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="min-w-0 space-y-3">
        <CornerEditor
          source={session.image.source}
          corners={session.corners}
          onChange={(next, final) => {
            if (final) session.setCorners(next);
          }}
        />
        <StraightenControls flow={flow} />
        {(flow.detectMissed || session.cornersFound === false) && (
          <p role="status" className="text-sm text-muted-foreground">
            {t('images.straighten.detectNone')}
          </p>
        )}
      </div>
      <div className="min-w-0 space-y-2">
        <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          {t('images.straighten.preview')}
        </span>
        <LivePreview
          engine={session.engine}
          params={flow.params}
          label={t('images.straighten.preview')}
          className="block h-auto max-h-96 w-full rounded-md border border-border object-contain"
        />
        <p className="text-sm text-muted-foreground">{t('images.straighten.hint')}</p>
      </div>
    </div>
  );
}

/** The corrected image and the AI's result side by side, one to choose (D19: never in its place). */
function AiCompare({ flow, dataUrl }: { readonly flow: ImagesFlow; readonly dataUrl: string }) {
  const { t } = useTranslation('tools');
  const uid = useId();
  const { useAiResult, setUseAiResult } = flow.ai;
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-semibold">{t('images.ai.choose')}</legend>
      <div className="grid grid-cols-2 gap-3">
        <div
          className={cn(
            'space-y-2 rounded-lg border p-2',
            !useAiResult ? 'border-ring' : 'border-border',
          )}
        >
          <LivePreview
            engine={flow.session.engine}
            params={flow.params}
            label={t('images.ai.original')}
            className="block h-auto w-full rounded-md"
          />
          <span className="flex items-center gap-2 text-sm">
            <input
              id={`${uid}-original`}
              type="radio"
              name={`${uid}-choice`}
              checked={!useAiResult}
              onChange={() => setUseAiResult(false)}
            />
            <label htmlFor={`${uid}-original`}>{t('images.ai.original')}</label>
          </span>
        </div>
        <div
          className={cn(
            'space-y-2 rounded-lg border p-2',
            useAiResult ? 'border-ring' : 'border-border',
          )}
        >
          <img
            src={dataUrl}
            alt={t('images.ai.improved')}
            className="block h-auto w-full rounded-md"
          />
          <span className="flex items-center gap-2 text-sm">
            <input
              id={`${uid}-improved`}
              type="radio"
              name={`${uid}-choice`}
              checked={useAiResult}
              onChange={() => setUseAiResult(true)}
            />
            <label htmlFor={`${uid}-improved`}>{t('images.ai.improved')}</label>
          </span>
        </div>
      </div>
      <p role="note" className="text-sm font-medium text-foreground">
        {t('images.ai.caution')}
      </p>
    </fieldset>
  );
}

function PresetPicker({ flow }: { readonly flow: ImagesFlow }) {
  const { t } = useTranslation('tools');
  const { corr } = flow;
  return (
    <div
      role="group"
      aria-label={t('images.correct.label')}
      className="grid grid-cols-2 gap-2 sm:grid-cols-4"
    >
      {PRESETS.map((preset) => (
        <Button
          key={preset.id}
          type="button"
          variant="outline"
          aria-pressed={corr.presetId === preset.id}
          onClick={() => corr.change({ preset: preset.id, settings: preset.settings })}
          className={cn(
            'h-11',
            corr.presetId === preset.id && 'border-ring bg-selected text-selected-foreground',
          )}
        >
          {t(`images.correct.presets.${preset.id}`)}
        </Button>
      ))}
    </div>
  );
}

function AdvancedSliders({ flow }: { readonly flow: ImagesFlow }) {
  const { t } = useTranslation('tools');
  const uid = useId();
  const { corr } = flow;
  const { settings } = corr;
  return (
    <details className="border-t border-border pt-3">
      <summary className="cursor-pointer text-sm font-semibold">
        {t('images.correct.advanced')}
      </summary>
      <div className="mt-3 space-y-3">
        {ENHANCEMENT_KEYS.map((key) => (
          <label key={key} htmlFor={`${uid}-${key}`} className="block text-sm">
            <span className="flex justify-between">
              <span>{t(`images.correct.${key}`)}</span>
              <span className="tabular-nums text-muted-foreground">
                {Math.round(settings[key] * 100)}
              </span>
            </span>
            <input
              id={`${uid}-${key}`}
              type="range"
              min={0}
              max={100}
              value={Math.round(settings[key] * 100)}
              onChange={(e) =>
                corr.change({ settings: { ...settings, [key]: Number(e.target.value) / 100 } })
              }
              className="mt-1 w-full accent-[var(--color-brand)]"
            />
          </label>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => corr.change({ settings: presetById(corr.presetId).settings })}
        >
          {t('images.correct.reset')}
        </Button>
      </div>
    </details>
  );
}

function AiPanel({ flow }: { readonly flow: ImagesFlow }) {
  const { t } = useTranslation('tools');
  const { ai, setAi } = flow.ai;
  return (
    <div className="space-y-2 rounded-lg bg-selected p-4">
      <p className="flex items-center gap-2 font-semibold text-selected-foreground">
        <Sparkles aria-hidden="true" className="size-4" />
        {t('images.ai.title')}
      </p>
      <p className="text-sm text-muted-foreground">{t('images.ai.text')}</p>
      <Button
        type="button"
        variant="outline"
        disabled={ai.status === 'working'}
        onClick={() => setAi({ status: 'confirming' })}
      >
        <Sparkles aria-hidden="true" />
        {ai.status === 'working' ? t('images.ai.working') : t('images.ai.button')}
      </Button>
      {ai.status === 'error' && (
        <p role="alert" className="text-sm text-destructive">
          {t(`images.ai.${ai.message}`)}
        </p>
      )}
    </div>
  );
}

export function CorrectStep({ flow }: { readonly flow: ImagesFlow }) {
  const { t } = useTranslation('tools');
  // «Migliora con IA» is offered only to whoever holds tools:ai-enhance; the server re-checks.
  const canEnhance = holds(useToolsPermissions(), TOOLS_AI_ENHANCE);
  const { ai } = flow.ai;
  if (!flow.session.image) return null;
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <div className="min-w-0 space-y-3">
        {ai.status === 'done' ? (
          <AiCompare flow={flow} dataUrl={ai.dataUrl} />
        ) : (
          <LivePreview
            engine={flow.session.engine}
            params={flow.params}
            label={t('images.straighten.preview')}
            className="block h-auto max-h-[32rem] w-full rounded-md border border-border object-contain"
          />
        )}
      </div>
      <div className="min-w-0 space-y-4">
        <PresetPicker flow={flow} />
        <AdvancedSliders flow={flow} />
        {canEnhance && <AiPanel flow={flow} />}
      </div>
    </div>
  );
}

export function DownloadStep({ flow }: { readonly flow: ImagesFlow }) {
  const { t } = useTranslation('tools');
  const uid = useId();
  if (!flow.result) return null;
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <img
        src={flow.result}
        alt={t('images.download.label')}
        className="block h-auto max-h-[32rem] w-full rounded-md border border-border object-contain"
      />
      <div className="min-w-0 space-y-4">
        {flow.ai.useAiResult && (
          <p className="text-sm font-medium">{t('images.download.fromAi')}</p>
        )}
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold">{t('images.download.format')}</legend>
          <div className="flex gap-4">
            {(['jpeg', 'png'] as const).map((f) => (
              <label key={f} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name={`${uid}-format`}
                  checked={flow.format === f}
                  onChange={() => flow.setFormat(f)}
                />
                {t(`images.download.${f}`)}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => void flow.download()}>
            <Download aria-hidden="true" />
            {t('images.download.button')}
          </Button>
          <Button type="button" variant="outline" onClick={flow.startOver}>
            {t('images.startOver')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function StepNav({ flow }: { readonly flow: ImagesFlow }) {
  const { t } = useTranslation('tools');
  const { step } = flow;
  const hideNote = step === 'correct' && flow.ai.ai.status === 'done' && flow.ai.useAiResult;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
      {hideNote ? <span /> : <LocalNote />}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={() => flow.move(-1)}>
          <ArrowLeft aria-hidden="true" />
          {t('images.back')}
        </Button>
        {step !== 'download' && (
          <Button
            type="button"
            disabled={flow.ai.ai.status === 'working'}
            onClick={() => flow.move(1)}
          >
            {t('images.next')}
            <ArrowRight aria-hidden="true" />
          </Button>
        )}
      </div>
    </div>
  );
}
